import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Loader2, LogOut, ShieldCheck } from 'lucide-react';
import QRCode from 'qrcode';
import { Button, Card, Field } from '@/components/ui';
import { api, ApiError, type ApiEnvelope } from '@/lib/api-client';
import { CampoCodigo } from './campo-codigo';

/**
 * Configurar el segundo factor — tarea 8.17
 *
 * La API le exige el segundo factor a todo administrador cuya empresa no lo
 * haya quitado a propósito, y mientras no lo tenga activo responde 403
 * `MFA_SETUP_REQUIRED` a TODO lo que no sea /auth. Esta pantalla es la otra
 * mitad: sin ella el administrador entraba, veía el menú y ninguna pantalla
 * cargaba.
 *
 * NO SE PUEDE CERRAR NI SALTAR, y no es de la interfaz: lo decide el servidor.
 * Sustituye a la pantalla que habría debajo.
 */
export function ConfigurarMfa({
  onTerminado,
  onSalir,
}: {
  onTerminado: () => void;
  onSalir: () => void;
}) {
  const queryClient = useQueryClient();
  const [qr, setQr] = useState<string | null>(null);
  const [secreto, setSecreto] = useState<string | null>(null);
  const [codigo, setCodigo] = useState('');
  const [copiado, setCopiado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const empezado = useRef(false);

  // Generar el secreto REEMPLAZA al anterior en el servidor, así que se pide
  // una sola vez por visita a la pantalla.
  const empezar = useMutation({
    mutationFn: () => api.post<ApiEnvelope<{ secreto: string; url: string }>>('/auth/mfa/empezar'),
    // El secreto se guarda en estado propio y no se lee de `empezar.data`: en
    // desarrollo, React monta, desmonta y vuelve a montar el componente, y el
    // observador de la mutación pierde el resultado en ese ir y venir. El QR
    // sí llegaba; la clave escrita a mano no.
    onSuccess: async (res) => {
      setSecreto(res.data.secreto);
      setQr(await QRCode.toDataURL(res.data.url, { margin: 1, width: 208 }));
    },
  });

  useEffect(() => {
    if (empezado.current) return;
    empezado.current = true;
    empezar.mutate();
  }, [empezar]);

  const confirmar = useMutation({
    mutationFn: () => api.post('/auth/mfa/confirmar', { codigo }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mfa-estado'] });
      onTerminado();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'No se ha podido comprobar el código.');
      setCodigo('');
    },
  });

  const copiar = async () => {
    if (!secreto) return;
    try {
      await navigator.clipboard.writeText(secreto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* sin permiso de portapapeles: queda el texto seleccionable */
    }
  };

  return (
    <div className="flex min-h-full items-start justify-center p-4 sm:items-center">
      <Card className="w-full max-w-md p-6">
        <div className="flex items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand-soft-fg">
            <ShieldCheck className="size-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-fg">Activa el segundo factor</h1>
            <p className="text-sm text-fg-muted">
              Tu empresa lo exige a los administradores. Son dos minutos.
            </p>
          </div>
        </div>

        <ol className="mt-5 space-y-5 text-sm text-fg">
          <li>
            <p className="font-medium">1. Instala una aplicación de autenticación</p>
            <p className="mt-0.5 text-fg-muted">
              Google Authenticator, Microsoft Authenticator, Authy, 1Password… sirve cualquiera.
            </p>
          </li>

          <li>
            <p className="font-medium">2. Escanea este código QR con ella</p>
            <div className="mt-3 flex flex-col items-center gap-3">
              {qr ? (
                <img
                  src={qr}
                  alt="Código QR para configurar el segundo factor"
                  width={208}
                  height={208}
                  className="rounded-lg border border-border bg-white"
                />
              ) : empezar.isError ? (
                <div className="text-center">
                  <p className="text-danger">No se ha podido generar el código.</p>
                  <Button size="sm" className="mt-2" onClick={() => empezar.mutate()}>
                    Reintentar
                  </Button>
                </div>
              ) : (
                <div className="flex size-52 items-center justify-center rounded-lg border border-border bg-surface-2">
                  <Loader2 className="size-6 animate-spin text-fg-subtle" />
                </div>
              )}

              {secreto && (
                <details className="w-full rounded-lg border border-border bg-surface-2 px-3 py-2">
                  <summary className="cursor-pointer text-fg-muted">
                    ¿No puedes escanearlo? Escribe la clave a mano
                  </summary>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="min-w-0 flex-1 break-all select-all font-mono text-xs text-fg">
                      {secreto.replace(/(.{4})/g, '$1 ').trim()}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={copiar}
                      aria-label="Copiar la clave"
                    >
                      {copiado ? <Check /> : <Copy />}
                    </Button>
                  </div>
                </details>
              )}
            </div>
          </li>

          <li>
            <p className="font-medium">3. Escribe el código de 6 dígitos que te muestra</p>
            <form
              className="mt-3 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                confirmar.mutate();
              }}
            >
              <Field label="Código" htmlFor="codigo-mfa" error={error ?? undefined}>
                <CampoCodigo
                  id="codigo-mfa"
                  value={codigo}
                  onChange={setCodigo}
                  invalid={!!error}
                />
              </Field>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={codigo.length !== 6 || !secreto || confirmar.isPending}
              >
                {confirmar.isPending && <Loader2 className="animate-spin" />}
                Activar y continuar
              </Button>
            </form>
          </li>
        </ol>

        <p className="mt-5 rounded-lg bg-warning-soft p-3 text-xs text-fg">
          Si pierdes el móvil sin haber guardado la clave, no podrás entrar. Guarda la clave de
          arriba (la de «escribirla a mano») en tu gestor de contraseñas.
        </p>

        <button
          type="button"
          onClick={onSalir}
          className="mx-auto mt-4 flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg"
        >
          <LogOut className="size-4" />
          Cerrar sesión
        </button>
      </Card>
    </div>
  );
}
